import React, { useState } from 'react';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../../config/firebase';

const QUOTES = [
  { text: "Moreover, it is required of stewards that they be found faithful.", ref: "1 Corinthians 4:2" },
  { text: "Each of you should use whatever gift you have received to serve others, as faithful stewards of God's grace.", ref: "1 Peter 4:10" },
  { text: "Honest scales and balances belong to the Lord; all the weights in the bag are of his making.", ref: "Proverbs 16:11" },
  { text: "A false balance is an abomination to the Lord, but a just weight is his delight.", ref: "Proverbs 11:1" },
  { text: "Whoever can be trusted with very little can also be trusted with much.", ref: "Luke 16:10" },
  { text: "Be sure you know the condition of your flocks, give careful attention to your herds.", ref: "Proverbs 27:23" },
  { text: "Honor the Lord with your wealth, with the firstfruits of all your crops.", ref: "Proverbs 3:9" },
];

const Login = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [quote] = useState(() => QUOTES[Math.floor(Math.random() * QUOTES.length)]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error('Google sign-in error:', err.code, err.message);
      setError(`Sign in failed: ${err.code || err.message}`);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* Left — brand panel */}
      <div
        className="hidden lg:flex flex-col justify-between w-1/2 p-14"
        style={{ background: 'linear-gradient(160deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)' }}
      >
        {/* Logo mark */}
        <div>
          <div className="mb-16">
            {/* White wordmark version — transparent icon bg, bars stay amber, text white */}
            <svg width="200" height="50" viewBox="0 0 400 100" xmlns="http://www.w3.org/2000/svg">
              <rect x="25" y="20" width="22" height="6" rx="3" fill="#854F0B"/>
              <rect x="18" y="31" width="36" height="7" rx="3" fill="#BA7517"/>
              <rect x="10" y="43" width="52" height="10" rx="3" fill="#EF9F27"/>
              <text x="88" y="54" textAnchor="start"
                fontFamily="'Century Gothic', 'Trebuchet MS', 'Gill Sans', sans-serif"
                fontSize="54" fontWeight="400" fill="#ffffff" letterSpacing="-1">blessio</text>
            </svg>
            <div className="text-indigo-300 text-xs tracking-widest uppercase mt-1 font-medium">Books</div>
          </div>

          <div className="mb-10">
            <h1 className="text-white text-4xl font-bold leading-tight mb-5">
              Financial clarity<br />for your calling.
            </h1>
            <p className="text-indigo-200 text-base leading-relaxed max-w-xs italic">
              "{quote.text}"
            </p>
            <p className="text-indigo-400 text-xs mt-2 not-italic">— {quote.ref}</p>
          </div>

          {/* Decorative rule */}
          <div className="w-12 h-0.5 mb-10" style={{ background: '#fbbf24' }} />

          <div className="space-y-3 text-indigo-200 text-sm">
            <div>Transaction tracking — bills &amp; receipts</div>
            <div>Multi-fund accounting</div>
            <div>Monthly reconciliation</div>
            <div>Budget vs. actual reporting</div>
            <div>Role-based access for your team</div>
          </div>
        </div>

        <p className="text-indigo-500 text-xs">
          © {new Date().getFullYear()} Blessio · Built for non-profits
        </p>
      </div>

      {/* Right — sign in */}
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-sm">

          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-10">
            <img src="/blessio-logo.svg" alt="Blessio" className="h-10 mx-auto" />
            <div className="text-slate-500 text-xs tracking-widest uppercase mt-2">Books</div>
          </div>

          {/* Desktop logo (right panel) */}
          <div className="hidden lg:flex items-center gap-2.5 mb-8">
            <svg viewBox="0 0 72 72" className="w-9 h-9 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="0" width="72" height="72" rx="16" fill="#2C2C2A"/>
              <rect x="25" y="20" width="22" height="6" rx="3" fill="#854F0B"/>
              <rect x="18" y="31" width="36" height="7" rx="3" fill="#BA7517"/>
              <rect x="10" y="43" width="52" height="10" rx="3" fill="#EF9F27"/>
            </svg>
            <span className="text-xl font-bold text-slate-800">Blessio</span>
          </div>

          <h2 className="text-2xl font-bold text-slate-800 mb-1">Welcome back</h2>
          <p className="text-slate-500 text-sm mb-8">
            Sign in to access your organization's books.
          </p>

          {error && (
            <div className="text-red-600 text-sm mb-5 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
              {error}
            </div>
          )}

          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-white border border-gray-200 rounded-2xl px-4 py-3.5 text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading ? 'Signing in…' : 'Continue with Google'}
          </button>

          <p className="text-xs text-slate-500 text-center mt-6 leading-relaxed">
            Access is by invitation only.<br />Contact your administrator to request access.
          </p>
        </div>
      </div>

    </div>
  );
};

export default Login;
